import { Box } from '@deckee/deck-hand';
import React, { FC, KeyboardEvent, MouseEvent } from 'react';
import { useTheme } from 'styled-components';

const STAR_VIEWBOX = '0 0 50 50';

// Outline: outer + inner subpaths form a filled ring (no stroke).
const WEATHER_FAVOURITE_STAR_OUTLINE_PATH =
  'M25 1a1 1 0 0 0-.932.639l-6.166 15.896L.95 18.4a1 1 0 0 0-.582 1.774l13.201 10.793-4.336 16.377a1 1 0 0 0 1.508 1.097L25 39.29l14.26 9.152a1 1 0 0 0 1.508-1.097l-4.336-16.377 13.2-10.793a1 1 0 0 0-.581-1.774l-16.953-.865L25.932 1.64A1 1 0 0 0 25 1m0 3.764 5.467 14.097a1 1 0 0 0 .883.637l15.01.768-11.692 9.56a1 1 0 0 0-.334 1.03l3.842 14.513-12.635-8.111a1 1 0 0 0-1.082 0l-12.635 8.111 3.842-14.514a1 1 0 0 0-.334-1.029l-11.691-9.56 15.01-.768a1 1 0 0 0 .882-.637z';

// Filled: outer subpath only.
const WEATHER_FAVOURITE_STAR_FILLED_PATH =
  'M25 1a1 1 0 0 0-.932.639l-6.166 15.896L.95 18.4a1 1 0 0 0-.582 1.774l13.201 10.793-4.336 16.377a1 1 0 0 0 1.508 1.097L25 39.29l14.26 9.152a1 1 0 0 0 1.508-1.097l-4.336-16.377 13.2-10.793a1 1 0 0 0-.581-1.774l-16.953-.865L25.932 1.64A1 1 0 0 0 25 1z';

interface FavouriteStarProps {
  filled?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  size?: number;
  /** Match the 40px leading icon slot used by LocationLockup / BlueCircleWrapper */
  iconSlot?: boolean;
  'data-testid'?: string;
}

const LOCATION_LOCKUP_ICON_SLOT_SIZE = 40;

const FavouriteStar: FC<FavouriteStarProps> = ({
  filled = false,
  onClick,
  size = 24,
  iconSlot = false,
  'data-testid': dataTestId,
}) => {
  const theme = useTheme();
  const starColor = theme.colors.primary;
  const outerSize = iconSlot ? LOCATION_LOCKUP_ICON_SLOT_SIZE : size;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flex={`0 0 ${outerSize}px`}
      width={`${outerSize}px`}
      height={`${outerSize}px`}
      flexShrink={0}
      onClick={onClick}
      data-testid={dataTestId}
      aria-hidden={onClick ? undefined : true}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event as unknown as MouseEvent<HTMLDivElement>);
              }
            }
          : undefined
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={STAR_VIEWBOX}
        fill="none"
        aria-hidden
      >
        <path
          d={
            filled
              ? WEATHER_FAVOURITE_STAR_FILLED_PATH
              : WEATHER_FAVOURITE_STAR_OUTLINE_PATH
          }
          fill={starColor}
        />
      </svg>
    </Box>
  );
};

export default FavouriteStar;
